/**
 * React bindings of `@tcg/realtime` (T14-07): one provider per app, `useRealtime(topics, onChange)`
 * for a screen, `useRealtimeStatus()` + `<RealtimeStatus />` for the small "● Live" indicator.
 */
import {
  createContext,
  type CSSProperties,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import type { RealtimeClient } from "../client";
import { indicatorOf, labelsFor, type RealtimeLabels } from "../i18n";
import type { ChangeMessage } from "../sse";
import type { ConnectionStatus, ResyncReason, TopicStatus } from "../types";

/** Query keys a change message invalidates (see `@tcg/realtime/query` `createQueryKeyMap`). */
export type KeyMapFn = (message: ChangeMessage) => ReadonlyArray<readonly unknown[]>;

export interface RealtimeContextValue {
  client: RealtimeClient | null;
  locale: string;
  labels?: Partial<RealtimeLabels>;
  /** Used by `@tcg/realtime/query` (default: `createQueryKeyMap()`). */
  keyMap?: KeyMapFn;
  /** Invalidation debounce of `@tcg/realtime/query` (default 250 ms). */
  debounceMs?: number;
}

const RealtimeContext = createContext<RealtimeContextValue>({ client: null, locale: "en" });

export interface RealtimeProviderProps extends Partial<Omit<RealtimeContextValue, "client">> {
  /** The tab's client (`createRealtimeClient(...)`); `null` = realtime off (screens poll). */
  client: RealtimeClient | null;
  children?: ReactNode;
}

export function RealtimeProvider({
  client,
  locale = "en",
  labels,
  keyMap,
  debounceMs,
  children,
}: RealtimeProviderProps) {
  const value = useMemo<RealtimeContextValue>(
    () => ({
      client,
      locale,
      ...(labels ? { labels } : {}),
      ...(keyMap ? { keyMap } : {}),
      ...(debounceMs !== undefined ? { debounceMs } : {}),
    }),
    [client, locale, labels, keyMap, debounceMs],
  );
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtimeContext(): RealtimeContextValue {
  return useContext(RealtimeContext);
}

export function useRealtimeClient(): RealtimeClient | null {
  return useContext(RealtimeContext).client;
}

export interface UseRealtimeOptions {
  enabled?: boolean;
  /** Refetch what the screen shows (hub lost track / tab came back / 30 s fallback poll). */
  onResync?: (reason: ResyncReason) => void;
  /** Token of the tracking link (`order:<number>` / `order.tracking:<number>` topics). */
  orderToken?: string | null;
  /** The `t` of a digital receipt link (`receipt:<receiptNo>` — T14-16); same slot as `orderToken`. */
  receiptToken?: string | null;
}

function combined(states: readonly TopicStatus[]): TopicStatus {
  if (states.includes("fallback")) {
    return "fallback";
  }
  return states.length > 0 && states.every((s) => s === "live") ? "live" : "connecting";
}

/**
 * Subscribe this component to `topics` while mounted: `onChange` runs for every change message of
 * them (refetch — messages never carry the data, except per-service `payload`). Returns `live`
 * while changes arrive, `connecting`, or `fallback` (not live: the client calls `onResync("poll")`
 * every 30 s). The callbacks may change between renders without resubscribing.
 */
export function useRealtime(
  topics: readonly string[],
  onChange?: (message: ChangeMessage) => void,
  options: UseRealtimeOptions = {},
): TopicStatus {
  const client = useRealtimeClient();
  const enabled = (options.enabled ?? true) && topics.length > 0 && client !== null;
  const [status, setStatus] = useState<TopicStatus>("connecting");
  const handlers = useRef({ onChange, onResync: options.onResync });
  useEffect(() => {
    handlers.current = { onChange, onResync: options.onResync };
  });
  const key = [...new Set(topics)].join(",");
  const orderToken = options.orderToken ?? options.receiptToken ?? null;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const releaseToken = orderToken ? holdOrderToken(client, orderToken) : null;
    const list = key.split(",");
    const states = new Map<string, TopicStatus>();
    // one refetch for a resync of the whole connection, not one per topic
    let queued: ResyncReason | null = null;
    let active = true;
    const resync = (reason: ResyncReason) => {
      if (queued === null) {
        queued = reason;
        queueMicrotask(() => {
          const r = queued ?? reason;
          queued = null;
          if (active) {
            handlers.current.onResync?.(r);
          }
        });
      }
    };
    const release = list.map((topic) =>
      client.subscribe(topic, {
        onChange: (message) => handlers.current.onChange?.(message),
        onResync: resync,
        onStatus: (next) => {
          states.set(topic, next);
          if (states.size === list.length) {
            setStatus(combined([...states.values()]));
          }
        },
      }),
    );
    return () => {
      active = false;
      release.forEach((unsubscribe) => {
        unsubscribe();
      });
      releaseToken?.();
    };
  }, [client, enabled, key, orderToken]);

  return enabled ? status : "fallback";
}

const tokenHolds = new WeakMap<RealtimeClient, { token: string; count: number }>();

/**
 * The tracking-link token is set on the shared client while a component that brought it is
 * mounted; the last one to unmount clears it (T14-07 review: it stayed for the life of the tab).
 */
function holdOrderToken(client: RealtimeClient, token: string): () => void {
  const held = tokenHolds.get(client);
  if (held?.token === token) {
    held.count += 1;
  } else {
    tokenHolds.set(client, { token, count: 1 });
    client.setOrderToken(token);
  }
  return () => {
    const current = tokenHolds.get(client);
    if (current?.token !== token) {
      return; // another link took over meanwhile
    }
    current.count -= 1;
    if (current.count === 0) {
      tokenHolds.delete(client);
      client.setOrderToken(null);
    }
  };
}

/** Alias kept for the apps' interim hooks (storefront / admin `useRealtimeTopics`). */
export const useRealtimeTopics = useRealtime;

const noop = () => () => undefined;

/** The client's overall state (`idle` without a client or topics). */
export function useRealtimeStatus(): ConnectionStatus {
  const client = useRealtimeClient();
  return useSyncExternalStore(
    client ? (cb) => client.onStatus(cb) : noop,
    () => client?.status() ?? "idle",
    () => "idle",
  );
}

export interface RealtimeStatusProps {
  className?: string;
  style?: CSSProperties;
  /** Override the provider's locale / labels. */
  locale?: string;
  labels?: Partial<RealtimeLabels>;
  /** A state to show instead of the client's (tests / stories). */
  status?: ConnectionStatus;
}

const COLORS = {
  live: "var(--color-status-active, #05a66b)",
  reconnecting: "var(--color-status-pending, #f59e0b)",
  fallback: "var(--color-status-pending, #f59e0b)",
  offline: "var(--color-status-problem, #d92d20)",
} as const;

/**
 * "● Live" / "○ Reconnecting…" / "○ Updates every 30 s" / "○ Offline" — colour + word (design/04
 * §0, never colour alone), `role="status"` so screen readers hear the change. Hidden when nothing
 * is subscribed. Styling: `className` (the dot uses the `--color-status-*` tokens of `@tcg/ui`).
 */
export function RealtimeStatus({ className, style, locale, labels, status }: RealtimeStatusProps) {
  const context = useRealtimeContext();
  const current = useRealtimeStatus();
  const state = indicatorOf(status ?? current);
  if (state === null) {
    return null;
  }
  const words = labelsFor(locale ?? context.locale, { ...context.labels, ...labels });
  const text = words[state];
  return (
    <span
      role="status"
      aria-live="polite"
      data-realtime-status={state}
      title={state === "live" ? words.liveHint : text}
      className={className}
      style={{ display: "inline-flex", alignItems: "center", gap: "0.35em", ...style }}
    >
      <span aria-hidden="true" style={{ color: COLORS[state] }}>
        {state === "live" ? "●" : "○"}
      </span>
      <span>{text}</span>
    </span>
  );
}
